import { useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMediaQuery } from "@/app/useMediaQuery";
import { ApiError } from "@/api/client";
import { fetchManualImage, useManualChapter, useManualToc } from "@/api/manual";
import { EmptyState, ErrorNote, Loading, PageTitle, linkClass } from "@/components/ui";
import { useI18n, useT } from "@/i18n/LocaleProvider";
import { renderManual } from "@/manual/render";

const INDEX_SLUG = "index";

/** The manual's own two-letter language: `de-DE` → `de`, `en-GB` → `en`. */
function languageOf(locale: string): string {
  return locale.split("-")[0]?.toLowerCase() ?? "de";
}

/**
 * The user manual (ADR 0008): browsable Markdown, fetched with the same bearer
 * token as everything else and rendered rather than hand-written as React, so
 * a chapter is edited as a Markdown file and nothing else.
 *
 * The route is `manual/*`: the wildcard is the chapter slug, and an empty one
 * (bare `.../manual`) is `index` rather than a separate redirect — there is
 * nowhere else for it to go, so there is nothing to redirect from.
 */
export function ManualPage() {
  const t = useT();
  const { locale } = useI18n();
  const language = languageOf(locale);
  const { householdId = "", "*": splat } = useParams();
  const slug = splat || INDEX_SLUG;
  const manualBasePath = `/households/${householdId}/manual`;
  const wide = useMediaQuery("(min-width: 1024px)");

  const toc = useManualToc(language);
  const chapter = useManualChapter(language, slug);

  const tocHtml = useMemo(
    () => (toc.data ? renderManual(toc.data, { currentPath: "toc.md", manualBasePath }) : ""),
    [toc.data, manualBasePath],
  );
  const chapterHtml = useMemo(
    () =>
      chapter.data
        ? renderManual(chapter.data, { currentPath: `${slug}.md`, manualBasePath })
        : "",
    [chapter.data, slug, manualBasePath],
  );

  const notFound = chapter.error instanceof ApiError && chapter.error.status === 404;
  const tocFailed = Boolean(toc.error);
  const chapterFailed = Boolean(chapter.error) && !notFound;

  const tocNav = (
    <>
      {toc.isLoading && <Loading />}
      {tocFailed && <ManualLoadError error={toc.error} />}
      {tocHtml && (
        <ManualContent
          html={tocHtml}
          language={language}
          className="manual-prose manual-toc"
        />
      )}
    </>
  );

  return (
    <div>
      <PageTitle>{t("nav.manual")}</PageTitle>

      <div className="lg:grid lg:grid-cols-[16rem_1fr] lg:items-start lg:gap-8">
        {wide ? (
          <nav
            aria-label={t("manual.contents")}
            className="lg:sticky lg:top-24"
            data-testid="manual-toc"
          >
            {tocNav}
          </nav>
        ) : (
          <details className="mb-6 rounded-panel border border-line bg-surface">
            <summary className="cursor-pointer select-none rounded-panel px-4 py-3 text-sm font-medium text-ink">
              {t("manual.contents")}
            </summary>
            <div className="border-t border-line px-4 py-3" data-testid="manual-toc">
              {tocNav}
            </div>
          </details>
        )}

        <div className="min-w-0" data-testid="manual-chapter">
          {chapter.isLoading && <Loading />}
          {notFound && (
            <EmptyState title={t("manual.notFound")}>
              <Link to={manualBasePath} className={linkClass}>
                {t("manual.backToIndex")}
              </Link>
            </EmptyState>
          )}
          {chapterFailed && <ManualLoadError error={chapter.error} />}
          {chapterHtml && !notFound && !chapterFailed && (
            <ManualContent
              html={chapterHtml}
              language={language}
              className="manual-prose max-w-prose"
              scrollOnChange
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ManualLoadError({ error }: { error: unknown }) {
  const t = useT();
  return (
    <EmptyState title={t("manual.loadError")}>
      <ErrorNote error={error} />
    </EmptyState>
  );
}

/**
 * Sanitised HTML, dropped into the DOM, with the two behaviours a static
 * `dangerouslySetInnerHTML` cannot give it on its own:
 *
 *  - A click on a rewritten chapter link (`data-manual-link`, from
 *    `renderManual`) is intercepted and handed to the router, so moving
 *    between chapters never reloads the page.
 *  - Each rewritten image (`data-manual-src`) is fetched through the
 *    authorised `fetchManualImage` and turned into an object URL, which is
 *    revoked again when the chapter changes or the page unmounts — otherwise
 *    every screenshot a person has ever opened in the session stays decoded
 *    in memory.
 */
function ManualContent({
  html,
  language,
  className,
  scrollOnChange = false,
}: {
  html: string;
  language: string;
  className: string;
  /** Only the chapter body scrolls on its own change; the toc nav should not. */
  scrollOnChange?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    function onClick(event: MouseEvent) {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest("a[data-manual-link]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      event.preventDefault();
      navigate(href);
    }

    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [navigate]);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const images = Array.from(root.querySelectorAll<HTMLImageElement>("img[data-manual-src]"));
    const objectUrls: string[] = [];
    let cancelled = false;

    for (const img of images) {
      const path = img.getAttribute("data-manual-src");
      if (!path) continue;
      fetchManualImage(language, path)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          objectUrls.push(url);
          img.src = url;
        })
        .catch(() => {
          // A missing or unreadable screenshot should not take the rest of
          // the chapter down with it.
        });
    }

    return () => {
      cancelled = true;
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [html, language]);

  // The contents list marks the chapter being read. It is set on the DOM rather
  // than in the Markdown because the same toc.md serves every chapter.
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    // A bare `.../manual` is the index chapter.
    const current = location.pathname.replace(/\/manual\/?$/, "/manual/index");
    root.querySelectorAll("a[data-manual-link]").forEach((anchor) => {
      const target = anchor.getAttribute("href")?.split("#")[0];
      if (target === current) anchor.setAttribute("aria-current", "page");
      else anchor.removeAttribute("aria-current");
    });
  }, [html, location.pathname]);

  useEffect(() => {
    if (!scrollOnChange) return;
    try {
      if (location.hash) {
        document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView();
      } else {
        window.scrollTo(0, 0);
      }
    } catch {
      // jsdom (unit tests) has no layout engine and throws "not implemented"
      // for both calls; a real browser always has one.
    }
  }, [scrollOnChange, location.pathname, location.hash]);

  // One object per `html`, not one per render: React compares this prop by
  // identity and rewrites the whole subtree when it changes, which would throw
  // away every loaded screenshot and the contents' current-chapter mark each
  // time this re-renders (which it does on every navigation).
  const inner = useMemo(() => ({ __html: html }), [html]);

  return <div ref={ref} className={className} dangerouslySetInnerHTML={inner} />;
}
